import { metrics } from '@atlassian/browser-metrics';
import type { Metric } from '@atlassian/browser-metrics/types';

/**
 * Possible chat mode that WILL impact latency metrics significantly, e.g. deepResearch
 * webSearch will not impact latency metrics, so it's not included here
 */
type ChatModeParams = {
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	isRovoCustomAgent?: boolean;
};

const SEND_MESSAGE_METRICS_KEY = 'ai-mate.send-message';
const TIME_TO_FIRST_ANSWER_PART_METRICS_KEY = 'ai-mate.time-to-first-answer-part';
const TIME_TO_FIRST_ACTION_PART_METRICS_KEY = 'ai-mate.time-to-first-action-part';
const TIME_TO_ACTION_COMPLETION_METRICS_KEY = 'ai-mate.time-to-action-completion';
const BETWEEN_CHUNKS_LATENCY_METRICS_KEY = 'ai-mate.between-chunks-latency';

// Reconnection metrics
const TIME_TO_CONNECT_TO_STREAM_METRICS_KEY = 'ai-mate.reconnect.time-to-connect-to-stream';
const TIME_TO_FIRST_CHUNK_RECEIVED_METRICS_KEY = 'ai-mate.reconnect.time-to-first-chunk-received';

export const interactionMetrics = {
	initializeChannels: metrics.interaction({
		key: 'ai-mate.initialize-channels',
	}),
	timeToRenderMessageContent: metrics.interaction({
		key: 'ai-mate.time-to-render-message-content',
	}),
	timeToRenderFinalResponse: metrics.interaction({
		key: 'ai-mate.time-to-render-final-response',
	}),
	sendConfirmedToolsTimeToFinishStream: metrics.interaction({
		key: 'ai-mate.send-confirmed-tools.time-to-finish-stream',
	}),
	sendConfirmedToolsTimeToFirstAnswerPart: metrics.interaction({
		key: 'ai-mate.send-confirmed-tools.time-to-first-answer-part',
	}),
	messageCompositionTime: metrics.interaction({
		key: 'ai-mate.message-composition-time',
	}),
} satisfies Record<string, Metric>;

export const getMessageMetricsNameWithParams = (
	baseName: string,
	{ deepResearchEnabled, thinkDeeperEnabled, isRovoCustomAgent }: ChatModeParams,
): string => {
	if (isRovoCustomAgent) {
		return `${baseName}.rovo-custom-agent`;
	}

	if (deepResearchEnabled) {
		return `${baseName}.deep-research`;
	}
	if (thinkDeeperEnabled) {
		return `${baseName}.think-deeper`;
	}

	return baseName;
};

export const messageLatencyMetrics = {
	createSendMessageMetrics: (params: ChatModeParams) => {
		return metrics.interaction({
			key: getMessageMetricsNameWithParams(SEND_MESSAGE_METRICS_KEY, params),
		});
	},
	createTimeToFirstAnswerPartMetrics: (params: ChatModeParams) => {
		return metrics.interaction({
			key: getMessageMetricsNameWithParams(TIME_TO_FIRST_ANSWER_PART_METRICS_KEY, params),
		});
	},
	createTimeToFirstActionPartMetrics: (params: ChatModeParams) => {
		return metrics.interaction({
			key: getMessageMetricsNameWithParams(TIME_TO_FIRST_ACTION_PART_METRICS_KEY, params),
		});
	},
	createTimeToActionCompletionMetrics: (params: ChatModeParams) => {
		return metrics.interaction({
			key: getMessageMetricsNameWithParams(TIME_TO_ACTION_COMPLETION_METRICS_KEY, params),
		});
	},
	createBetweenChunksLatencyMetrics: (params: ChatModeParams) => {
		return metrics.interaction({
			key: getMessageMetricsNameWithParams(BETWEEN_CHUNKS_LATENCY_METRICS_KEY, params),
		});
	},
};

export const reconnectionMetrics = {
	createTimeToConnectToStreamMetrics: (params: ChatModeParams) => {
		return metrics.interaction({
			key: getMessageMetricsNameWithParams(TIME_TO_CONNECT_TO_STREAM_METRICS_KEY, params),
		});
	},
	createTimeToResumeStreamMetrics: (params: ChatModeParams) => {
		return metrics.interaction({
			key: getMessageMetricsNameWithParams(TIME_TO_FIRST_CHUNK_RECEIVED_METRICS_KEY, params),
		});
	},
};

export const deadAirMetrics = {
	createDeadAirIntervalMetrics: () => {
		return metrics.interaction({
			key: 'ai-mate.dead-air-interval',
		});
	},
	createDeadAirMaxMetrics: () => {
		return metrics.interaction({
			key: 'ai-mate.dead-air-max',
		});
	},
};
